import * as React from "react";

import {defaultProps, type Props} from "./props.ts";
import type {ConfettiHandle, Particle, Shape} from "./types.ts";

const random = (low: number, high: number) =>
    Math.random() * (high - low) + low;
const sample = (array: Array<Shape>): Shape =>
    array[Math.floor(random(0, array.length))];
const getDiameter = (width: number, height: number) =>
    Math.sqrt(width * width + height * height);

const PIXEL_RATIO = window.devicePixelRatio || 1;

function isCanvasSupported() {
    // jsdom provides a canvas stub in tests, but it just logs the error:
    //     Not implemented: HTMLCanvasElement.prototype.getContext
    if (process.env.NODE_ENV === "test") {
        return false;
    }
    const elem = document.createElement("canvas");
    return !!(elem.getContext && elem.getContext("2d"));
}

const Confetti = React.forwardRef<ConfettiHandle, Props>((props, ref) => {
    const hasCanvasSupport = React.useMemo(() => isCanvasSupported(), []);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const ctxRef = React.useRef<CanvasRenderingContext2D | null>(null);
    const particlesRef = React.useRef<Array<Particle>>([]);

    React.useEffect(() => {
        if (!hasCanvasSupport) {
            return;
        }

        ctxRef.current = canvasRef.current?.getContext("2d") ?? null;

        if (ctxRef.current) {
            ctxRef.current.setTransform(PIXEL_RATIO, 0, 0, PIXEL_RATIO, 0, 0);
            ctxRef.current.clearRect(
                0,
                0,
                canvasRef.current?.width ?? 0,
                canvasRef.current?.height ?? 0,
            );
        }
    }, [hasCanvasSupport]);

    const tick = React.useCallback(() => {
        const now = Date.now();
        const {
            height = defaultProps.height,
            width = defaultProps.width,
            gravity = defaultProps.gravity,
        } = props;

        ctxRef.current?.clearRect(0, 0, width, height);

        for (let i = 0; i < particlesRef.current.length; i++) {
            const particle = particlesRef.current[i];
            const age = (now - particle.birth) / 1000;
            if (age < 0) {
                continue;
            }

            const x = particle.x + particle.vx * age;
            const y =
                particle.y + particle.vy * age + (gravity * age * age) / 2;

            const diameter = getDiameter(
                particle.width * particle.scale,
                particle.height * particle.scale,
            );

            const isOffScreen =
                x + diameter < 0 ||
                x - diameter > width ||
                y - diameter > height;

            if (isOffScreen) {
                if (i === particlesRef.current.length - 1) {
                    particlesRef.current.pop();
                } else {
                    particlesRef.current[i] = particlesRef.current.pop()!;
                    i--;
                }
                continue;
            }

            ctxRef.current?.translate(x, y);

            const currentSpin = particle.angle + particle.spin * age;
            ctxRef.current?.rotate(currentSpin);

            const twistAmount = particle.twist
                ? Math.cos(particle.angle + particle.twist * age)
                : 1;

            const imageElem = twistAmount >= 0 ? particle.front : particle.back;

            ctxRef.current?.scale(twistAmount, 1);

            ctxRef.current?.drawImage(
                imageElem,
                imageElem.naturalWidth * particle.scale * -0.5,
                imageElem.naturalHeight * particle.scale * -0.5,
                imageElem.naturalWidth * particle.scale,
                imageElem.naturalHeight * particle.scale,
            );

            ctxRef.current?.setTransform(PIXEL_RATIO, 0, 0, PIXEL_RATIO, 0, 0);
        }

        if (particlesRef.current.length) {
            // eslint-disable-next-line no-restricted-syntax -- no hooks-based API for requestAnimationFrame
            requestAnimationFrame(tick);
        } else if (props.onComplete) {
            props.onComplete();
        }
    }, [props]);

    const start = React.useCallback(
        (clientX: number, clientY: number) => {
            const {
                shapes = defaultProps.shapes,
                emitDuration = defaultProps.emitDuration,
                numParticles = defaultProps.numParticles,
                minSpeed = defaultProps.minSpeed,
                maxSpeed = defaultProps.maxSpeed,
                minScale = defaultProps.minScale,
                maxScale = defaultProps.maxScale,
                spin = defaultProps.spin,
                twist = defaultProps.twist,
            } = props;

            if (shapes.length === 0) {
                return;
            }

            const rect = canvasRef.current?.getBoundingClientRect();
            const cx = clientX - (rect?.left ?? 0);
            const cy = clientY - (rect?.top ?? 0);

            if (!particlesRef.current.length) {
                // eslint-disable-next-line no-restricted-syntax -- no hooks-based API for requestAnimationFrame
                requestAnimationFrame(tick);
            }

            for (let i = 0; i < numParticles; i++) {
                const speed = random(minSpeed, maxSpeed);
                const directionOffset = -Math.PI / 2 + 0.7 * random(-0.5, 0.5);
                const {front, back} = sample(shapes);

                particlesRef.current.push({
                    birth: Date.now() + random(0, emitDuration),
                    x:
                        cx +
                        (props.spreadHorizontally
                            ? random(-0.5, 0.5) * (rect?.width ?? 0)
                            : 0),
                    y: cy,
                    spin: spin * random(-0.5, 0.5),
                    twist: twist * random(-0.5, 0.5),
                    angle: random(0, 2 * Math.PI),
                    scale: random(minScale, maxScale),
                    vx: Math.cos(directionOffset) * speed,
                    vy: Math.sin(directionOffset) * speed,
                    front,
                    back,
                    width: front.naturalWidth,
                    height: front.naturalHeight,
                });
            }
        },
        [props, tick],
    );

    React.useImperativeHandle(ref, () => ({
        startConfetti: start,
    }));

    if (!hasCanvasSupport) {
        return null;
    }

    const {height = defaultProps.height, width = defaultProps.width} = props;

    return (
        <canvas
            height={String(height * PIXEL_RATIO)}
            width={String(width * PIXEL_RATIO)}
            style={{width, height}}
            ref={canvasRef}
        />
    );
});

export default Confetti;
